import React, { useState } from 'react';

// USCIS Notice Scanner - Step 3: Proceed With Response?
export const USCISStep3ProceedDecision = ({ formData, setFormData, onNext, onBack }) => {
  const [decision, setDecision] = useState(formData.proceedDecision || '');

  const handleDecision = (choice) => {
    setDecision(choice);
    setFormData({ ...formData, proceedDecision: choice });
    
    if (choice === 'no') {
      // Save breakdown summary and exit
      const summary = {
        notice_analyzed: formData.parsedResults?.notice_type || 'USCIS Notice',
        case_number: formData.parsedResults?.case_number || 'N/A',
        deadline: formData.parsedResults?.deadline || 'N/A',
        main_issues: formData.breakdown?.core_issues?.length || 0,
        saved_date: new Date().toISOString()
      };
      setFormData({ ...formData, savedSummary: summary });
      // Could redirect to summary page or close modal
    } else {
      // Continue to next step
      setTimeout(() => onNext(), 500);
    }
  };

  const parsedResults = formData.parsedResults || {};
  const breakdown = formData.breakdown || {};

  // Show required documents from the analysis
  const getRequiredDocuments = () => {
    const required = parsedResults.required_exhibits || [];
    const coreIssues = breakdown.core_issues || [];
    
    const dynamicRequirements = [];
    coreIssues.forEach(issue => {
      if (issue.actionable_items) {
        dynamicRequirements.push(...issue.actionable_items);
      }
    });
    
    return [...required, ...dynamicRequirements].slice(0, 8); // Limit to 8 items
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">🤔 Proceed With Response Preparation?</h3>
        <p className="text-gray-400 text-lg">Based on our analysis, would you like to continue with response preparation?</p>
      </div>

      {/* Analysis Summary */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📋 Analysis Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-gray-400 text-sm">Notice Type</p>
            <p className="text-white font-semibold">{parsedResults.notice_type}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Response Deadline</p>
            <p className="text-red-400 font-semibold">{parsedResults.deadline}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Core Issues Identified</p>
            <p className="text-white font-semibold">{breakdown.core_issues?.length || 0} major issues</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Estimated Success Rate</p>
            <p className="text-green-400 font-semibold">{breakdown.response_strategy?.success_likelihood || `${Math.min(85, Math.max(60, Math.round(((breakdown.core_issues?.length || 0) * 10) + 55)))}%`}</p>
          </div>
        </div>
      </div>

      {/* What Happens Next */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Continue Path */}
        <div className="bg-green-900/20 border border-green-500 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-3">✅ If You Continue</h4>
          <ul className="space-y-2">
            <li className="flex items-start text-gray-300">
              <span className="text-green-400 mr-2">•</span>
              Document upload portal for evidence collection
            </li>
            <li className="flex items-start text-gray-300">
              <span className="text-green-400 mr-2">•</span>
              AI-powered evidence gap analysis
            </li>
            <li className="flex items-start text-gray-300">
              <span className="text-green-400 mr-2">•</span>
              Professional affidavit and cover letter templates
            </li>
            <li className="flex items-start text-gray-300">
              <span className="text-green-400 mr-2">•</span>
              Complete response packet generation
            </li>
            <li className="flex items-start text-gray-300">
              <span className="text-green-400 mr-2">•</span>
              Quality assurance review before submission
            </li>
          </ul>
        </div>

        {/* Exit Path */}
        <div className="bg-yellow-900/20 border border-yellow-500 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-3">📄 If You Exit Now</h4>
          <ul className="space-y-2">
            <li className="flex items-start text-gray-300">
              <span className="text-yellow-400 mr-2">•</span>
              Receive detailed breakdown summary document
            </li>
            <li className="flex items-start text-gray-300">
              <span className="text-yellow-400 mr-2">•</span>
              Keep analysis results for future reference
            </li>
            <li className="flex items-start text-gray-300">
              <span className="text-yellow-400 mr-2">•</span>
              No response documents generated
            </li>
            <li className="flex items-start text-gray-300">
              <span className="text-yellow-400 mr-2">•</span>
              Can return later to continue response preparation
            </li>
          </ul>
        </div>
      </div>

      {/* Decision Buttons */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-6 text-center">What would you like to do?</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => handleDecision('yes')}
            className={`p-6 rounded-lg border-2 transition-all duration-300 ${
              decision === 'yes'
                ? 'border-green-500 bg-green-900/20'
                : 'border-gray-600 hover:border-green-500 hover:bg-green-900/10'
            }`}
          >
            <div className="text-center">
              <div className="text-4xl mb-2">✅</div>
              <h5 className="text-lg font-semibold text-white mb-2">Yes, Continue</h5>
              <p className="text-gray-400 text-sm">Proceed with full response preparation</p>
            </div>
          </button>

          <button
            onClick={() => handleDecision('no')}
            className={`p-6 rounded-lg border-2 transition-all duration-300 ${
              decision === 'no'
                ? 'border-yellow-500 bg-yellow-900/20'
                : 'border-gray-600 hover:border-yellow-500 hover:bg-yellow-900/10'
            }`}
          >
            <div className="text-center">
              <div className="text-4xl mb-2">📄</div>
              <h5 className="text-lg font-semibold text-white mb-2">No, Just Summary</h5>
              <p className="text-gray-400 text-sm">Save analysis summary and exit</p>
            </div>
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back to Analysis
        </button>
        {decision && (
          <div className="text-gray-400 text-sm">
            {decision === 'yes' ? 'Proceeding to document upload...' : 'Preparing summary for download...'}
          </div>
        )}
      </div>
    </div>
  );
};

// USCIS Notice Scanner - Step 4: Document Upload Portal
export const USCISStep4DocumentUpload = ({ formData, setFormData, onNext, onBack }) => {
  const [uploadedDocuments, setUploadedDocuments] = useState(formData.responseDocuments || []);
  const [selectedCategory, setSelectedCategory] = useState('');

  const documentCategories = [
    { id: 'uscis-forms', label: 'USCIS Forms & Notices', icon: '📋' },
    { id: 'identity', label: 'Identity Documents', icon: '🆔' },
    { id: 'financial', label: 'Financial Evidence', icon: '💰' },
    { id: 'relationship', label: 'Relationship Evidence', icon: '💑' },
    { id: 'employment', label: 'Employment Documents', icon: '💼' },
    { id: 'legal', label: 'Legal Documents', icon: '⚖️' },
    { id: 'medical', label: 'Medical Records', icon: '🏥' },
    { id: 'travel', label: 'Travel Documents', icon: '✈️' },
    { id: 'other', label: 'Other Supporting Evidence', icon: '📄' }
  ];

  const handleFileUpload = async (files) => {
    if (!selectedCategory) {
      alert('Please select a document category first');
      return;
    }

    const newFiles = [];
    
    // Process each file with actual document analysis
    for (const file of Array.from(files)) {
      try {
        // Convert file to base64 for AI analysis
        const fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Call backend for actual document analysis with authentication
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        
        console.log(`🔍 Analyzing document: ${file.name} (${file.type}, ${file.size} bytes)`);
        
        const response = await fetch(`${backendUrl}/api/ai/analyze-uploaded-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',  // Include cookies for authentication
          body: JSON.stringify({
            document_file: fileData,
            document_name: file.name,
            document_type: selectedCategory,
            case_context: {
              notice_type: formData.parsedResults?.notice_type,
              case_number: formData.parsedResults?.case_number,
              main_issues: formData.breakdown?.core_issues
            }
          })
        });

        let documentAnalysis;
        if (response.ok) {
          documentAnalysis = await response.json();
          console.log(`✅ Document analysis successful for ${file.name}:`, documentAnalysis);
        } else {
          const errorText = await response.text();
          console.error(`❌ Document analysis failed for ${file.name}:`, response.status, errorText);
          // Fallback if analysis fails
          documentAnalysis = {
            document_type: selectedCategory,
            content_description: `Document analysis failed: ${errorText}`,
            case_relevance: "Manual review required to determine relevance",
            exhibit_description: `Exhibit: ${file.name} requiring detailed manual review`,
            immigration_value: "Medium",
            analysis_error: `Analysis failed: ${errorText}`
          };
        }

        // Create file object with analysis results
        const fileWithAnalysis = {
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          category: selectedCategory,
          categoryLabel: documentCategories.find(c => c.id === selectedCategory)?.label || 'Other',
          uploadDate: new Date().toISOString(),
          file: file,
          // Add actual analysis results
          analysis: documentAnalysis,
          content_description: documentAnalysis.content_description,
          parties_mentioned: documentAnalysis.parties_mentioned,
          key_dates: documentAnalysis.key_dates,
          case_relevance: documentAnalysis.case_relevance,
          exhibit_description: documentAnalysis.exhibit_description,
          immigration_value: documentAnalysis.immigration_value,
          red_flags: documentAnalysis.red_flags,
          authenticity_indicators: documentAnalysis.authenticity_indicators
        };

        newFiles.push(fileWithAnalysis);
      } catch (error) {
        console.error('Document analysis error:', error);
        
        // Add document with error indication
        const fileWithError = {
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          category: selectedCategory,
          categoryLabel: documentCategories.find(c => c.id === selectedCategory)?.label || 'Other',
          uploadDate: new Date().toISOString(),
          file: file,
          analysis_error: "Analysis failed - manual review required",
          content_description: `Document analysis failed for ${file.name}`,
          case_relevance: "Manual review required",
          exhibit_description: `Exhibit: ${file.name} (manual review required)`,
          immigration_value: "Unknown"
        };

        newFiles.push(fileWithError);
      }
    }
    
    const updatedFiles = [...uploadedDocuments, ...newFiles];
    setUploadedDocuments(updatedFiles);
    setFormData({ ...formData, responseDocuments: updatedFiles });
  };

  const removeDocument = (docId) => {
    const updatedFiles = uploadedDocuments.filter(doc => doc.id !== docId);
    setUploadedDocuments(updatedFiles);
    setFormData({ ...formData, responseDocuments: updatedFiles });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentsByCategory = () => {
    const grouped = {};
    uploadedDocuments.forEach(doc => {
      if (!grouped[doc.category]) {
        grouped[doc.category] = [];
      }
      grouped[doc.category].push(doc);
    });
    return grouped;
  };

  const parsedResults = formData.parsedResults || {};
  const breakdown = formData.breakdown || {};

  // Show required documents from the analysis
  const getRequiredDocuments = () => {
    const required = parsedResults.required_exhibits || [];
    const coreIssues = breakdown.core_issues || [];
    
    const dynamicRequirements = [];
    coreIssues.forEach(issue => {
      if (issue.actionable_items) {
        dynamicRequirements.push(...issue.actionable_items);
      }
    });
    
    return [...required, ...dynamicRequirements].slice(0, 8); // Limit to 8 items
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">📁 Document Upload Portal</h3>
        <p className="text-gray-400 text-lg">Upload evidence to respond to your {parsedResults.notice_type}</p>
      </div>

      {/* Deadline Alert */}
      <div className="bg-red-900/20 border border-red-500 p-4 rounded-lg">
        <div className="flex items-center">
          <span className="text-2xl mr-3">⏰</span>
          <div>
            <p className="text-red-400 font-semibold">Response Deadline: {parsedResults.deadline}</p>
            <p className="text-gray-300 text-sm">
              {parsedResults.response_deadline_days} days remaining to submit your response
            </p>
          </div>
        </div>
      </div>

      {/* Required Documents from Analysis */}
      <div className="bg-blue-900/20 border border-blue-500 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-white mb-4">📋 Required Evidence from Your {parsedResults.notice_type}</h4>
        <p className="text-gray-400 mb-4">Based on the AI analysis of your notice, you need to provide:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {getRequiredDocuments().map((requirement, index) => (
            <div key={index} className="flex items-start bg-blue-800/20 p-3 rounded-lg">
              <span className="text-blue-400 mr-2 mt-1">📄</span>
              <span className="text-gray-300 text-sm">{requirement}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Document Category Selection */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-white mb-4">📂 Select Document Category</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {documentCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`p-3 rounded-lg border-2 transition-all duration-300 ${
                selectedCategory === category.id
                  ? 'border-blue-500 bg-blue-900/20'
                  : 'border-gray-600 hover:border-gray-500'
              }`}
            >
              <div className="text-center">
                <div className="text-2xl mb-1">{category.icon}</div>
                <p className="text-white text-sm font-medium">{category.label}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* File Upload Zone */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-white mb-4">
          📤 Upload Files {selectedCategory && `to ${documentCategories.find(c => c.id === selectedCategory)?.label}`}
        </h4>
        
        <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          selectedCategory ? 'border-blue-600 hover:border-blue-500' : 'border-gray-600'
        }`}>
          <div className="text-4xl mb-3">📄</div>
          <p className="text-white mb-2">
            {selectedCategory ? 'Drop files here or click to browse' : 'Select a category first'}
          </p>
          <p className="text-gray-400 text-sm mb-4">Supports PDF, DOC, DOCX, JPG, PNG files</p>
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={(e) => handleFileUpload(e.target.files)}
            disabled={!selectedCategory}
            className="hidden"
            id="document-upload"
          />
          <label
            htmlFor="document-upload"
            className={`inline-block px-6 py-3 rounded-lg cursor-pointer transition-colors ${
              selectedCategory
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }`}
          >
            Choose Files
          </label>
        </div>
      </div>

      {/* Uploaded Documents by Category */}
      {Object.keys(getDocumentsByCategory()).length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-4">
            📎 Uploaded Documents ({uploadedDocuments.length} total)
          </h4>
          
          {Object.entries(getDocumentsByCategory()).map(([categoryId, docs]) => {
            const category = documentCategories.find(c => c.id === categoryId);
            return (
              <div key={categoryId} className="mb-6">
                <h5 className="text-white font-medium mb-3 flex items-center">
                  <span className="mr-2">{category?.icon}</span>
                  {category?.label} ({docs.length})
                </h5>
                <div className="space-y-2">
                  {docs.map((doc) => (
                    <div key={doc.id} className="bg-gray-700 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">📄</span>
                          <div>
                            <p className="text-white font-medium">{doc.name}</p>
                            <p className="text-gray-400 text-sm">{formatFileSize(doc.size)} • {doc.categoryLabel}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeDocument(doc.id)}
                          className="text-red-400 hover:text-red-300 text-xl"
                        >
                          ×
                        </button>
                      </div>
                      
                      {/* Show actual document analysis */}
                      {doc.analysis && (
                        <div className="bg-gray-800 p-3 rounded-lg mt-3">
                          <p className="text-blue-400 font-medium mb-2">📋 Document Analysis:</p>
                          <div className="space-y-2 text-sm">
                            {doc.content_description && (
                              <p className="text-gray-300">
                                <span className="text-blue-300 font-medium">Content:</span> {doc.content_description}
                              </p>
                            )}
                            {doc.case_relevance && (
                              <p className="text-gray-300">
                                <span className="text-green-300 font-medium">Relevance:</span> {doc.case_relevance}
                              </p>
                            )}
                            {doc.exhibit_description && (
                              <p className="text-gray-300">
                                <span className="text-purple-300 font-medium">Exhibit:</span> {doc.exhibit_description}
                              </p>
                            )}
                            {doc.immigration_value && (
                              <p className="text-gray-300">
                                <span className="text-yellow-300 font-medium">Value:</span> {doc.immigration_value}
                              </p>
                            )}
                            {doc.parties_mentioned && doc.parties_mentioned.length > 0 && (
                              <p className="text-gray-300">
                                <span className="text-cyan-300 font-medium">Parties:</span> {doc.parties_mentioned.join(', ')}
                              </p>
                            )}
                            {doc.key_dates && doc.key_dates.length > 0 && (
                              <p className="text-gray-300">
                                <span className="text-orange-300 font-medium">Key Dates:</span> {doc.key_dates.join(', ')}
                              </p>
                            )}
                            {doc.red_flags && doc.red_flags.length > 0 && (
                              <p className="text-red-300">
                                <span className="font-medium">⚠️ Concerns:</span> {doc.red_flags.join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Show error if analysis failed */}
                      {doc.analysis_error && (
                        <div className="bg-red-900/20 border border-red-500 p-3 rounded-lg mt-3">
                          <p className="text-red-400 font-medium mb-1">⚠️ Analysis Failed:</p>
                          <p className="text-red-300 text-sm">{doc.analysis_error}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Guidelines */}
      <div className="bg-blue-900/20 border border-blue-500 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-white mb-4">📝 Upload Guidelines</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-blue-400 font-medium mb-2">Best Practices:</p>
            <ul className="space-y-1 text-gray-300 text-sm">
              <li>• Scan documents at high resolution (300 DPI minimum)</li>
              <li>• Ensure all text is clearly readable</li>
              <li>• Include all pages of multi-page documents</li>
              <li>• Use descriptive file names</li>
            </ul>
          </div>
          <div>
            <p className="text-yellow-400 font-medium mb-2">Required Evidence:</p>
            <ul className="space-y-1 text-gray-300 text-sm">
              {parsedResults.required_exhibits?.slice(0, 4).map((exhibit, index) => (
                <li key={index}>• {exhibit}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
        >
          Continue to Evidence Strategy →
        </button>
      </div>
    </div>
  );
};